// Grower-bin holdings — the pure half of the Grower bins tab.
//
// The register groups and totals rows; the three dialogs validate typed input
// and shape the row they write. None of that needs React, and all of it is
// worth testing: a holding is a DEBT the packhouse owes a grower, so a bin
// count that gets silently dropped or halved is fruit-bin custody the grower
// never gets back. Keeping these rules out of the components means they can be
// asserted without mounting a dialog.

import { ValidationError } from '@/lib/errorReporter'
import { isStaleWriteError } from '@/lib/optimistic'
import { CONTAINER_LABEL, type ContainerType } from '@/lib/containerTypes'

export interface GrowerHolding {
  holding_id:     string
  grower_id:      string
  grower_name:    string
  container_type: ContainerType
  bin_type_id:    string | null
  bin_count:      number
  status:         'AT_PACKHOUSE' | 'RETURNED' | 'CANCELLED'
  received_at:    string
  returned_at:    string | null
  delivery_id:    string | null
  vehicle_reg:    string | null
  driver_name:    string | null
  notes:          string | null
  // Mig 372 — column added with bump-version trigger so the Return /
  // Edit dialogs can use updateWithVersion. A stale-write means a
  // parallel-tab admin already touched the row; the operator gets a
  // refresh prompt instead of silent data loss.
  version:        number
}

export const GROWER_HOLDING_COLS =
  'holding_id, grower_id, grower_name, container_type, bin_type_id, bin_count, status, received_at, returned_at, delivery_id, vehicle_reg, driver_name, notes, version'

export interface GrowerHoldingGroup {
  grower_id:   string
  grower_name: string
  rows:        GrowerHolding[]
}

/** One card per grower, growers A→Z, each grower's rows in the order the
 *  query returned them (newest first). */
export function groupHoldingsByGrower(rows: readonly GrowerHolding[]): GrowerHoldingGroup[] {
  const map = new Map<string, GrowerHoldingGroup>()
  for (const h of rows) {
    if (!map.has(h.grower_id)) map.set(h.grower_id, { grower_id: h.grower_id, grower_name: h.grower_name, rows: [] })
    map.get(h.grower_id)!.rows.push(h)
  }
  return Array.from(map.values()).sort((a, b) => a.grower_name.localeCompare(b.grower_name))
}

export function sumBins(rows: readonly { bin_count: number }[]): number {
  return rows.reduce((s, h) => s + h.bin_count, 0)
}

/** The container a holding is IN, as the operator named it. A custom bin type
 *  (Bin Types tab) is saved as bin_type_id with KF49_BIN as the enum stand-in
 *  (decodeContainerPickerValue), so labelling from container_type alone printed
 *  every custom type as "KF49 bin" — a grower's "Fresca Blue lug" read as our
 *  own bins the moment an admin corrected it. bin_type_id wins; a label not
 *  loaded yet stays neutral rather than borrowing the stand-in's name. */
export function holdingContainerLabel(
  h: Pick<GrowerHolding, 'container_type' | 'bin_type_id'>,
  binTypeLabels: ReadonlyMap<string, string>,
): string {
  if (h.bin_type_id) return binTypeLabels.get(h.bin_type_id) ?? 'Custom bin type'
  return CONTAINER_LABEL[h.container_type]
}

/** Gate intake: the grower bins we hold ARE the containers the fruit came in,
 *  so the grower-bin "Bin type" follows "Fruit arrived in" until the operator
 *  picks something else there. It used to sit on its Red arm-lug default
 *  whatever the fruit came in, and a truck delivered in Fresca lugs was logged
 *  under Grower bins as held Red arm-lugs. Values are picker values
 *  ('enum:…' | 'type:<uuid>'); '' is the fruit picker's "Not specified", which
 *  the grower-bin picker has no option for. */
export function growerBinTypeFollowingFruit(a: {
  growerBinType: string
  fruitBefore:   string
  fruitAfter:    string
  defaultType:   string
  /** True once the operator chose the grower-bin type themselves. Then it is
   *  never replaced, even if the pick happens to equal the default or the
   *  current fruit container. The value match below is only the fallback for
   *  callers that cannot tell. */
  picked:        boolean
}): string {
  if (a.picked) return a.growerBinType
  const following = a.growerBinType === a.defaultType || a.growerBinType === a.fruitBefore
  if (!following) return a.growerBinType
  return a.fruitAfter || a.defaultType
}

/** Noon SAST, not midnight: the operator picks a calendar DAY, and a midnight
 *  stamp reads back as the day before in any timezone west of it. */
function holdingDateIso(ymd: string): string {
  return `${ymd}T12:00:00+02:00`
}

/** The date guard and the noon-SAST conversion are one step — a caller that
 *  skips the guard writes `undefinedT12:00:00+02:00`. */
export function requireHoldingDate(ymd: string, message: string): string {
  if (!ymd) throw new ValidationError(message)
  return holdingDateIso(ymd)
}

export function trimOrNull(v: string): string | null {
  return v.trim() || null
}

/** A holding with no bins is not a holding. `parseInt` keeps the historical
 *  leniency ("12 bins" → 12); zero, blank and negatives are refused rather
 *  than written, because a 0-bin row reads as "nothing owed". */
export function parseBinCount(raw: string, message: string): number {
  const n = parseInt(raw)
  if (!n || n <= 0) throw new ValidationError(message)
  return n
}

/** The return dialog APPENDS to the holding's notes — it must never replace
 *  them, or the receiving note ("dropped at the north gate") is lost. */
export function mergeHoldingNotes(existing: string | null, added: string): string | null {
  return [existing, trimOrNull(added)].filter(Boolean).join(' — ') || null
}

/** A version conflict is not a system fault — someone else edited the row in
 *  another tab. Turn it into a sentence the operator can act on; re-throw
 *  anything else untouched. */
export function asStaleWriteError(e: unknown, message: string): unknown {
  return isStaleWriteError(e) ? new ValidationError(message) : e
}

export interface ContainerColumns {
  container_type: ContainerType
  bin_type_id:    string | null
}

interface GrowerOption { grower_id: string; name: string }

/** Everything the Receive dialog must know before it touches the network,
 *  checked in the order the operator meets the fields. */
export function validateReceiveDraft(d: {
  growerId:   string
  growers:    readonly GrowerOption[]
  binCount:   string
  receivedAt: string
}): { grower: GrowerOption; bins: number; receivedAtIso: string } {
  if (!d.growerId) throw new ValidationError('Pick a grower')
  const bins = parseBinCount(d.binCount, 'Enter a bin count')
  const receivedAtIso = requireHoldingDate(d.receivedAt, 'Pick a date')
  const grower = d.growers.find(g => g.grower_id === d.growerId)
  if (!grower) throw new Error('Grower not found')
  return { grower, bins, receivedAtIso }
}

export function buildReceiveInsert(a: {
  growerId:      string
  grower:        GrowerOption
  bins:          number
  container:     ContainerColumns
  receivedAtIso: string
  receivedBy:    string | null
  vehicle:       string
  driver:        string
  notes:         string
}): Record<string, unknown> {
  return {
    grower_id:      a.growerId,
    grower_name:    a.grower.name,
    container_type: a.container.container_type,
    bin_type_id:    a.container.bin_type_id,
    bin_count:      a.bins,
    received_at:    a.receivedAtIso,
    received_by:    a.receivedBy,
    vehicle_reg:    trimOrNull(a.vehicle),
    driver_name:    trimOrNull(a.driver),
    notes:          trimOrNull(a.notes),
  }
}

export function buildReturnPatch(a: {
  returnedAtIso: string
  returnedBy:    string | null
  vehicle:       string
  driver:        string
  existingNotes: string | null
  notes:         string
}): Record<string, unknown> {
  return {
    status:        'RETURNED',
    returned_at:   a.returnedAtIso,
    returned_by:   a.returnedBy,
    vehicle_reg:   trimOrNull(a.vehicle),
    driver_name:   trimOrNull(a.driver),
    notes:         mergeHoldingNotes(a.existingNotes, a.notes),
    update_reason: 'returned to grower',
  }
}

/** The admin amendment. `status` is deliberately absent — a holding leaves
 *  AT_PACKHOUSE through Return, never through an edit. */
export function buildEditPatch(a: {
  binCount:     string
  receivedAt:   string
  updateReason: string
  container:    ContainerColumns
  vehicle:      string
  driver:       string
  notes:        string
}): Record<string, unknown> {
  const bins = parseBinCount(a.binCount, 'Bin count must be > 0')
  const receivedAtIso = requireHoldingDate(a.receivedAt, 'Received date is required')
  if (!a.updateReason.trim()) {
    throw new ValidationError('Reason for the edit is required — it lands in the audit trail.')
  }
  return {
    bin_count:      bins,
    container_type: a.container.container_type,
    bin_type_id:    a.container.bin_type_id,
    received_at:    receivedAtIso,
    vehicle_reg:    trimOrNull(a.vehicle),
    driver_name:    trimOrNull(a.driver),
    notes:          trimOrNull(a.notes),
    update_reason:  a.updateReason.trim(),
  }
}
